'use client';

/**
 * Stripe WebGL Gradient Animation
 * Original: https://gist.github.com/jordienr/64bcf75f8b08641f205bd6a1a0d4ce1d
 * All Credits to Stripe.com
 */

// Converting colors to proper format
function normalizeColor(hexCode: number): [number, number, number] {
  return [
    ((hexCode >> 16) & 255) / 255,
    ((hexCode >> 8) & 255) / 255,
    (255 & hexCode) / 255,
  ];
}

interface UniformOptions {
  value?: unknown;
  type?: string;
  excludeFrom?: string;
  transpose?: boolean;
}

interface AttributeOptions {
  target: number;
  size: number;
  type?: number;
  normalized?: boolean;
  values?: Float32Array | Uint16Array;
}

// MiniGl class - Essential functionality of WebGL
class MiniGl {
  canvas: HTMLCanvasElement;
  gl: WebGLRenderingContext;
  meshes: Mesh[] = [];
  commonUniforms: Record<string, Uniform> = {};
  width = 0;
  height = 0;
  debug: (...args: unknown[]) => void;
  lastDebugMsg = 0;

  // Inner classes (dynamically assigned in constructor)
  Material: any;
  Uniform: any;
  PlaneGeometry: any;
  Mesh: any;
  Attribute: any;

  constructor(
    canvas: HTMLCanvasElement,
    width?: number,
    height?: number,
    debugMode = false,
  ) {
    this.canvas = canvas;
    const gl = canvas.getContext('webgl', { antialias: true });
    if (!gl) throw new Error('WebGL not supported');
    this.gl = gl;

    const debugOutput =
      typeof window !== 'undefined' &&
      window.location.search.toLowerCase().indexOf('debug=webgl') !== -1;

    this.debug =
      debugMode && debugOutput
        ? (...args: unknown[]) => {
            const t = Date.now();
            if (t - this.lastDebugMsg > 1000) console.log('---');
            console.log(new Date().toLocaleTimeString(), ...args);
            this.lastDebugMsg = t;
          }
        : () => {};

    // Bind inner classes with context
    const context = this.gl;
    const miniGl = this;

    // Uniform class
    class UniformImpl {
      type: string;
      value: unknown;
      typeFn: string;
      excludeFrom?: string;
      transpose?: boolean;

      constructor(options: UniformOptions) {
        this.type = options.type || 'float';
        this.value = options.value;
        this.excludeFrom = options.excludeFrom;
        this.transpose = options.transpose;
        this.typeFn =
          {
            float: '1f',
            int: '1i',
            vec2: '2fv',
            vec3: '3fv',
            vec4: '4fv',
            mat4: 'Matrix4fv',
          }[this.type] || '1f';
      }

      update(location: WebGLUniformLocation | null): void {
        if (this.value === undefined || location === null) return;
        const fn = `uniform${this.typeFn}` as keyof WebGLRenderingContext;
        if (this.typeFn.indexOf('Matrix') === 0) {
          (context[fn] as Function)(
            location,
            this.transpose || false,
            this.value,
          );
        } else {
          (context[fn] as Function)(location, this.value);
        }
      }

      getDeclaration(name: string, type: string, length?: number): string {
        if (this.excludeFrom === type) return '';

        if ((this as unknown as { type: string }).type === 'array') {
          const arr = this.value as UniformImpl[];
          return (
            arr[0].getDeclaration(name, type, arr.length) +
            `\nconst int ${name}_length = ${arr.length};`
          );
        }

        if ((this as unknown as { type: string }).type === 'struct') {
          let nameNoPrefix = name.replace('u_', '');
          nameNoPrefix =
            nameNoPrefix.charAt(0).toUpperCase() + nameNoPrefix.slice(1);
          const fields = Object.entries(
            this.value as Record<string, UniformImpl>,
          )
            .map(([fieldName, uniform]) =>
              uniform.getDeclaration(fieldName, type).replace(/^uniform/, ''),
            )
            .join('');
          return `uniform struct ${nameNoPrefix} {\n${fields}\n} ${name}${length && length > 0 ? `[${length}]` : ''};`;
        }

        return `uniform ${this.type} ${name}${length && length > 0 ? `[${length}]` : ''};`;
      }
    }

    // Attribute class
    class AttributeImpl {
      target: number;
      size: number;
      type: number;
      normalized: boolean;
      buffer: WebGLBuffer | null;
      values?: Float32Array | Uint16Array;

      constructor(options: AttributeOptions) {
        this.target = options.target;
        this.size = options.size;
        this.type = options.type || context.FLOAT;
        this.normalized = options.normalized || false;
        this.buffer = context.createBuffer();
        this.values = options.values;
        this.update();
      }

      update(): void {
        if (this.values === undefined) return;
        context.bindBuffer(this.target, this.buffer);
        context.bufferData(this.target, this.values, context.STATIC_DRAW);
      }

      attach(name: string, program: WebGLProgram): number {
        const location = context.getAttribLocation(program, name);
        if (this.target === context.ARRAY_BUFFER) {
          context.enableVertexAttribArray(location);
          context.vertexAttribPointer(
            location,
            this.size,
            this.type,
            this.normalized,
            0,
            0,
          );
        }
        return location;
      }

      use(location: number): void {
        context.bindBuffer(this.target, this.buffer);
        if (this.target === context.ARRAY_BUFFER) {
          context.enableVertexAttribArray(location);
          context.vertexAttribPointer(
            location,
            this.size,
            this.type,
            this.normalized,
            0,
            0,
          );
        }
      }
    }

    // PlaneGeometry class
    class PlaneGeometryImpl {
      attributes: {
        position: AttributeImpl;
        uv: AttributeImpl;
        uvNorm: AttributeImpl;
        index: AttributeImpl;
      };
      width = 1;
      height = 1;
      orientation = 'xz';
      xSegCount = 1;
      ySegCount = 1;
      vertexCount = 4;
      quadCount = 2;

      constructor(
        width?: number,
        height?: number,
        xSeg?: number,
        ySeg?: number,
        orientation?: string,
      ) {
        this.attributes = {
          position: new AttributeImpl({
            target: context.ARRAY_BUFFER,
            size: 3,
          }),
          uv: new AttributeImpl({ target: context.ARRAY_BUFFER, size: 2 }),
          uvNorm: new AttributeImpl({ target: context.ARRAY_BUFFER, size: 2 }),
          index: new AttributeImpl({
            target: context.ELEMENT_ARRAY_BUFFER,
            size: 3,
            type: context.UNSIGNED_SHORT,
          }),
        };
        this.setTopology(xSeg, ySeg);
        this.setSize(width, height, orientation);
      }

      setTopology(xSeg = 1, ySeg = 1): void {
        this.xSegCount = xSeg;
        this.ySegCount = ySeg;
        this.vertexCount = (this.xSegCount + 1) * (this.ySegCount + 1);
        this.quadCount = this.xSegCount * this.ySegCount * 2;

        this.attributes.uv.values = new Float32Array(2 * this.vertexCount);
        this.attributes.uvNorm.values = new Float32Array(2 * this.vertexCount);
        this.attributes.index.values = new Uint16Array(3 * this.quadCount);

        for (let y = 0; y <= this.ySegCount; y++) {
          for (let x = 0; x <= this.xSegCount; x++) {
            const i = y * (this.xSegCount + 1) + x;
            this.attributes.uv.values[2 * i] = x / this.xSegCount;
            this.attributes.uv.values[2 * i + 1] = 1 - y / this.ySegCount;
            this.attributes.uvNorm.values[2 * i] = (x / this.xSegCount) * 2 - 1;
            this.attributes.uvNorm.values[2 * i + 1] =
              1 - (y / this.ySegCount) * 2;

            if (x < this.xSegCount && y < this.ySegCount) {
              const s = y * this.xSegCount + x;
              this.attributes.index.values[6 * s] = i;
              this.attributes.index.values[6 * s + 1] = i + 1 + this.xSegCount;
              this.attributes.index.values[6 * s + 2] = i + 1;
              this.attributes.index.values[6 * s + 3] = i + 1;
              this.attributes.index.values[6 * s + 4] = i + 1 + this.xSegCount;
              this.attributes.index.values[6 * s + 5] = i + 2 + this.xSegCount;
            }
          }
        }

        this.attributes.uv.update();
        this.attributes.uvNorm.update();
        this.attributes.index.update();
      }

      setSize(width = 1, height = 1, orientation = 'xz'): void {
        this.width = width;
        this.height = height;
        this.orientation = orientation;

        if (
          !this.attributes.position.values ||
          this.attributes.position.values.length !== 3 * this.vertexCount
        ) {
          this.attributes.position.values = new Float32Array(
            3 * this.vertexCount,
          );
        }

        const halfWidth = width / -2;
        const halfHeight = height / -2;
        const segWidth = width / this.xSegCount;
        const segHeight = height / this.ySegCount;

        for (let y = 0; y <= this.ySegCount; y++) {
          const posY = halfHeight + y * segHeight;
          for (let x = 0; x <= this.xSegCount; x++) {
            const posX = halfWidth + x * segWidth;
            const idx = y * (this.xSegCount + 1) + x;
            this.attributes.position.values[
              3 * idx + 'xyz'.indexOf(orientation[0])
            ] = posX;
            this.attributes.position.values[
              3 * idx + 'xyz'.indexOf(orientation[1])
            ] = -posY;
          }
        }

        this.attributes.position.update();
      }
    }

    // Material class
    class MaterialImpl {
      uniforms: Record<string, UniformImpl>;
      uniformInstances: {
        uniform: UniformImpl;
        location: WebGLUniformLocation | null;
      }[] = [];
      program: WebGLProgram;

      constructor(
        vertexSource: string,
        fragmentSource: string,
        uniforms: Record<string, UniformImpl> = {},
      ) {
        this.uniforms = uniforms;

        const getUniformDeclarations = (
          unis: Record<string, UniformImpl>,
          type: string,
        ) =>
          Object.entries(unis)
            .map(([name, uniform]) => uniform.getDeclaration(name, type))
            .filter(Boolean)
            .join('\n');

        const prefix = 'precision highp float;';

        const fullVertexSource = `
          ${prefix}
          attribute vec4 position;
          attribute vec2 uv;
          attribute vec2 uvNorm;
          ${getUniformDeclarations(miniGl.commonUniforms as Record<string, UniformImpl>, 'vertex')}
          ${getUniformDeclarations(uniforms, 'vertex')}
          ${vertexSource}
        `;

        const fullFragmentSource = `
          ${prefix}
          ${getUniformDeclarations(miniGl.commonUniforms as Record<string, UniformImpl>, 'fragment')}
          ${getUniformDeclarations(uniforms, 'fragment')}
          ${fragmentSource}
        `;

        const vertexShader = this.compileShader(
          context.VERTEX_SHADER,
          fullVertexSource,
        );
        const fragmentShader = this.compileShader(
          context.FRAGMENT_SHADER,
          fullFragmentSource,
        );

        this.program = context.createProgram()!;
        context.attachShader(this.program, vertexShader!);
        context.attachShader(this.program, fragmentShader!);
        context.linkProgram(this.program);

        if (!context.getProgramParameter(this.program, context.LINK_STATUS)) {
          console.error(
            'Program link error:',
            context.getProgramInfoLog(this.program),
          );
        }

        context.useProgram(this.program);
        this.attachUniforms(
          undefined,
          miniGl.commonUniforms as Record<string, UniformImpl>,
        );
        this.attachUniforms(undefined, uniforms);
      }

      compileShader(type: number, source: string): WebGLShader | null {
        const shader = context.createShader(type);
        if (!shader) return null;
        context.shaderSource(shader, source);
        context.compileShader(shader);
        if (!context.getShaderParameter(shader, context.COMPILE_STATUS)) {
          console.error(
            'Shader compile error:',
            context.getShaderInfoLog(shader),
          );
          console.error('Source:', source);
        }
        return shader;
      }

      attachUniforms(
        name: string | undefined,
        uniforms: Record<string, UniformImpl> | UniformImpl,
      ): void {
        if (name === undefined) {
          Object.entries(uniforms as Record<string, UniformImpl>).forEach(
            ([n, u]) => {
              this.attachUniforms(n, u);
            },
          );
        } else {
          const uniform = uniforms as UniformImpl;
          if ((uniform as unknown as { type: string }).type === 'array') {
            (uniform.value as UniformImpl[]).forEach((u, i) => {
              this.attachUniforms(`${name}[${i}]`, u);
            });
          } else if (
            (uniform as unknown as { type: string }).type === 'struct'
          ) {
            Object.entries(
              uniform.value as Record<string, UniformImpl>,
            ).forEach(([fieldName, u]) => {
              this.attachUniforms(`${name}.${fieldName}`, u);
            });
          } else {
            this.uniformInstances.push({
              uniform,
              location: context.getUniformLocation(this.program, name),
            });
          }
        }
      }
    }

    // Mesh class
    class MeshImpl {
      geometry: PlaneGeometryImpl;
      material: MaterialImpl;
      wireframe = false;
      attributeInstances: { attribute: AttributeImpl; location: number }[] = [];

      constructor(geometry: PlaneGeometryImpl, material: MaterialImpl) {
        this.geometry = geometry;
        this.material = material;

        Object.entries(this.geometry.attributes).forEach(
          ([name, attribute]) => {
            this.attributeInstances.push({
              attribute,
              location: attribute.attach(name, this.material.program),
            });
          },
        );

        miniGl.meshes.push(this as unknown as Mesh);
      }

      draw(): void {
        context.useProgram(this.material.program);
        this.material.uniformInstances.forEach(({ uniform, location }) => {
          uniform.update(location);
        });
        this.attributeInstances.forEach(({ attribute, location }) => {
          attribute.use(location);
        });
        context.drawElements(
          this.wireframe ? context.LINES : context.TRIANGLES,
          this.geometry.attributes.index.values!.length,
          context.UNSIGNED_SHORT,
          0,
        );
      }

      remove(): void {
        miniGl.meshes = miniGl.meshes.filter(
          (m) => m !== (this as unknown as Mesh),
        );
      }
    }

    this.Uniform = UniformImpl;
    this.Attribute = AttributeImpl;
    this.PlaneGeometry = PlaneGeometryImpl;
    this.Material = MaterialImpl;
    this.Mesh = MeshImpl;

    if (width && height) {
      this.setSize(width, height);
    }

    // Initialize common uniforms
    const identity = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
    this.commonUniforms = {
      projectionMatrix: new this.Uniform({ type: 'mat4', value: identity }),
      modelViewMatrix: new this.Uniform({ type: 'mat4', value: identity }),
      resolution: new this.Uniform({ type: 'vec2', value: [1, 1] }),
      aspectRatio: new this.Uniform({ type: 'float', value: 1 }),
    };
  }

  setSize(width = 640, height = 480): void {
    this.width = width;
    this.height = height;
    this.canvas.width = width;
    this.canvas.height = height;
    this.gl.viewport(0, 0, width, height);
    (this.commonUniforms.resolution as Uniform).value = [width, height];
    (this.commonUniforms.aspectRatio as Uniform).value = width / height;
  }

  setOrthographicCamera(
    left = 0,
    top = 0,
    bottom = 0,
    near = -2000,
    far = 2000,
  ): void {
    (this.commonUniforms.projectionMatrix as Uniform).value = [
      2 / this.width,
      0,
      0,
      0,
      0,
      2 / this.height,
      0,
      0,
      0,
      0,
      2 / (near - far),
      0,
      left,
      top,
      bottom,
      1,
    ];
  }

  render(): void {
    this.gl.clearColor(0, 0, 0, 0);
    this.gl.clearDepth(1);
    this.meshes.forEach((mesh) => mesh.draw());
  }
}

// Type aliases - using any for simplicity since inner classes are dynamically defined
type Uniform = any;
type PlaneGeometry = any;
type Material = any;
type Mesh = any;

// Shader sources
const shaderFiles = {
  noise: `
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

float snoise(vec3 v) {
  const vec2 C = vec2(1.0/6.0, 1.0/3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

  vec3 i = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);

  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);

  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;

  i = mod289(i);
  vec4 p = permute(permute(permute(
    i.z + vec4(0.0, i1.z, i2.z, 1.0))
    + i.y + vec4(0.0, i1.y, i2.y, 1.0))
    + i.x + vec4(0.0, i1.x, i2.x, 1.0));

  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;

  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);

  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);

  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);

  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);

  vec4 s0 = floor(b0) * 2.0 + 1.0;
  vec4 s1 = floor(b1) * 2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));

  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;

  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);

  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
  p0 *= norm.x;
  p1 *= norm.y;
  p2 *= norm.z;
  p3 *= norm.w;

  vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
  m = m * m;
  return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
}
`,
  blend: `
vec3 blendNormal(vec3 base, vec3 blend) { return blend; }
vec3 blendNormal(vec3 base, vec3 blend, float opacity) {
  return (blendNormal(base, blend) * opacity + base * (1.0 - opacity));
}
`,
  vertex: `
varying vec3 v_color;

void main() {
  float time = u_time * u_global.noiseSpeed;
  vec2 noiseCoord = resolution * uvNorm * u_global.noiseFreq;
  vec2 st = 1. - uvNorm.xy;

  // Tilting the plane
  float tilt = resolution.y / 2.0 * uvNorm.y;
  float incline = resolution.x * uvNorm.x / 2.0 * u_vertDeform.incline;
  float offset = resolution.x / 2.0 * u_vertDeform.incline * mix(u_vertDeform.offsetBottom, u_vertDeform.offsetTop, uv.y);

  // Vertex noise
  float noise = snoise(vec3(
    noiseCoord.x * u_vertDeform.noiseFreq.x + time * u_vertDeform.noiseFlow,
    noiseCoord.y * u_vertDeform.noiseFreq.y,
    time * u_vertDeform.noiseSpeed + u_vertDeform.noiseSeed
  )) * u_vertDeform.noiseAmp;

  noise *= 1.0 - pow(abs(uvNorm.y), 2.0);
  noise = max(0.0, noise);

  vec3 pos = vec3(
    position.x,
    position.y + tilt + incline + noise - offset,
    position.z
  );

  // Vertex color
  if (u_active_colors[0] == 1.) {
    v_color = u_baseColor;
  }

  for (int i = 0; i < u_waveLayers_length; i++) {
    if (u_active_colors[i + 1] == 1.) {
      WaveLayers layer = u_waveLayers[i];

      float layerNoise = smoothstep(
        layer.noiseFloor,
        layer.noiseCeil,
        snoise(vec3(
          noiseCoord.x * layer.noiseFreq.x + time * layer.noiseFlow,
          noiseCoord.y * layer.noiseFreq.y,
          time * layer.noiseSpeed + layer.noiseSeed
        )) / 2.0 + 0.5
      );

      v_color = blendNormal(v_color, layer.color, pow(layerNoise, 4.));
    }
  }

  gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
}
`,
  fragment: `
varying vec3 v_color;

void main() {
  vec3 color = v_color;
  if (u_darken_top == 1.0) {
    vec2 st = gl_FragCoord.xy / resolution.xy;
    color.g -= pow(st.y + sin(-12.0) * st.x, u_shadow_power) * 0.4;
  }
  gl_FragColor = vec4(color, 1.0);
}
`,
};

export interface StripeGradientConfig {
  colors?: string[];
  wireframe?: boolean;
  density?: [number, number];
  angle?: number;
  amplitude?: number;
  seed?: number;
  speed?: number;
  darkenTop?: boolean;
}

const defaultConfig: Required<StripeGradientConfig> = {
  colors: ['#ef008f', '#6ec3f4', '#7038ff', '#ffba27'],
  wireframe: false,
  density: [0.06, 0.16],
  angle: 0,
  amplitude: 320,
  seed: 5,
  speed: 1,
  darkenTop: false,
};

export class StripeGradient {
  private canvas: HTMLCanvasElement | null = null;
  private minigl: MiniGl | null = null;
  private mesh: Mesh | null = null;
  private material: Material | null = null;
  private geometry: PlaneGeometry | null = null;

  private config: Required<StripeGradientConfig>;
  private sectionColors: [number, number, number][] = [];
  private uniforms: Record<string, Uniform> = {};

  private t = 1253106;
  private last = 0;
  private isPlaying = false;
  private animationFrameId: number | null = null;

  private width = 0;
  private height = 600;
  private xSegCount = 0;
  private ySegCount = 0;

  constructor(config: Partial<StripeGradientConfig> = {}) {
    this.config = { ...defaultConfig, ...config };
  }

  connect(canvas: HTMLCanvasElement): boolean {
    this.canvas = canvas;

    try {
      this.minigl = new MiniGl(canvas, undefined, undefined, true);
    } catch (e) {
      console.error('Failed to create MiniGl:', e);
      return false;
    }

    this.initGradientColors();
    this.initMesh();
    this.resize();

    window.addEventListener('resize', this.handleResize);
    return true;
  }

  disconnect(): void {
    this.pause();
    window.removeEventListener('resize', this.handleResize);
    if (this.mesh) {
      this.mesh.remove();
    }
    this.minigl = null;
    this.canvas = null;
  }

  play(): void {
    if (this.isPlaying) return;
    this.isPlaying = true;
    this.last = 0;
    requestAnimationFrame(this.animate);
  }

  pause(): void {
    this.isPlaying = false;
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  private handleResize = (): void => {
    this.resize();
  };

  private resize(): void {
    if (!this.canvas || !this.minigl || !this.mesh) return;

    this.width = this.canvas.clientWidth || window.innerWidth;
    this.height = this.canvas.clientHeight || 600;

    this.minigl.setSize(this.width, this.height);
    this.minigl.setOrthographicCamera();

    this.xSegCount = Math.ceil(this.width * this.config.density[0]);
    this.ySegCount = Math.ceil(this.height * this.config.density[1]);

    (this.mesh as unknown as { geometry: PlaneGeometry }).geometry.setTopology(
      this.xSegCount,
      this.ySegCount,
    );
    (this.mesh as unknown as { geometry: PlaneGeometry }).geometry.setSize(
      this.width,
      this.height,
    );

    if (this.uniforms.u_shadow_power) {
      (this.uniforms.u_shadow_power as Uniform).value =
        this.width < 600 ? 5 : 6;
    }
  }

  private animate = (timestamp: number): void => {
    if (!this.isPlaying || !this.minigl) return;

    // Skip every other frame for performance
    if (this.last !== 0 && Math.floor(timestamp) % 2 === 0) {
      this.animationFrameId = requestAnimationFrame(this.animate);
      return;
    }

    const delta =
      this.last === 0 ? 16 : Math.min(timestamp - this.last, 1000 / 15);
    this.t += delta * this.config.speed;
    this.last = timestamp;

    if (this.uniforms.u_time) {
      (this.uniforms.u_time as Uniform).value = this.t;
    }

    this.minigl.render();
    this.animationFrameId = requestAnimationFrame(this.animate);
  };

  private initGradientColors(): void {
    this.sectionColors = this.config.colors.map((hex) => {
      const cleanHex = hex.startsWith('#') ? hex.slice(1) : hex;
      const fullHex =
        cleanHex.length === 3
          ? cleanHex
              .split('')
              .map((c) => c + c)
              .join('')
          : cleanHex;
      return normalizeColor(parseInt(fullHex, 16));
    });

    // Ensure at least 4 colors
    while (this.sectionColors.length < 4) {
      this.sectionColors.push([1, 1, 1]);
    }
  }

  private initMesh(): void {
    if (!this.minigl) return;

    const freqX = 0.00014;
    const freqY = 0.00029;
    const activeColors = [1, 1, 1, 1];

    // Create uniforms
    this.uniforms = {
      u_time: new this.minigl.Uniform({ value: 0 }),
      u_shadow_power: new this.minigl.Uniform({ value: 5 }),
      u_darken_top: new this.minigl.Uniform({
        value: this.config.darkenTop ? 1 : 0,
      }),
      u_active_colors: new this.minigl.Uniform({
        value: activeColors,
        type: 'vec4',
      }),
      u_global: new this.minigl.Uniform({
        value: {
          noiseFreq: new this.minigl.Uniform({
            value: [freqX, freqY],
            type: 'vec2',
          }),
          noiseSpeed: new this.minigl.Uniform({
            value: 5e-6 * this.config.speed,
          }),
        },
        type: 'struct',
      }),
      u_vertDeform: new this.minigl.Uniform({
        value: {
          incline: new this.minigl.Uniform({
            value: Math.sin(this.config.angle) / Math.cos(this.config.angle),
          }),
          offsetTop: new this.minigl.Uniform({ value: -0.5 }),
          offsetBottom: new this.minigl.Uniform({ value: -0.5 }),
          noiseFreq: new this.minigl.Uniform({ value: [3, 4], type: 'vec2' }),
          noiseAmp: new this.minigl.Uniform({ value: this.config.amplitude }),
          noiseSpeed: new this.minigl.Uniform({ value: 10 }),
          noiseFlow: new this.minigl.Uniform({ value: 3 }),
          noiseSeed: new this.minigl.Uniform({ value: this.config.seed }),
        },
        type: 'struct',
        excludeFrom: 'fragment',
      }),
      u_baseColor: new this.minigl.Uniform({
        value: this.sectionColors[0],
        type: 'vec3',
        excludeFrom: 'fragment',
      }),
      u_waveLayers: new this.minigl.Uniform({
        value: this.sectionColors.slice(1).map(
          (color, i) =>
            new this.minigl!.Uniform({
              value: {
                color: new this.minigl!.Uniform({ value: color, type: 'vec3' }),
                noiseFreq: new this.minigl!.Uniform({
                  value: [
                    2 + (i + 1) / this.sectionColors.length,
                    3 + (i + 1) / this.sectionColors.length,
                  ],
                  type: 'vec2',
                }),
                noiseSpeed: new this.minigl!.Uniform({
                  value: 11 + 0.3 * (i + 1),
                }),
                noiseFlow: new this.minigl!.Uniform({
                  value: 6.5 + 0.3 * (i + 1),
                }),
                noiseSeed: new this.minigl!.Uniform({
                  value: this.config.seed + 10 * (i + 1),
                }),
                noiseFloor: new this.minigl!.Uniform({ value: 0.1 }),
                noiseCeil: new this.minigl!.Uniform({
                  value: 0.63 + 0.07 * (i + 1),
                }),
              },
              type: 'struct',
            }),
        ),
        excludeFrom: 'fragment',
        type: 'array',
      }),
    };

    const vertexShader = [
      shaderFiles.noise,
      shaderFiles.blend,
      shaderFiles.vertex,
    ].join('\n\n');

    this.material = new this.minigl.Material(
      vertexShader,
      shaderFiles.fragment,
      this.uniforms as Record<string, Uniform>,
    );

    this.geometry = new this.minigl.PlaneGeometry();
    this.mesh = new this.minigl.Mesh(this.geometry, this.material);
  }
}
